import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { BrandLogo } from './BrandLogo';

const NAV_ITEMS = [
  { to: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
  { to: '/training', icon: 'school', label: 'Training' },
  { to: '/assistant', icon: 'auto_awesome', label: 'AI Chat' },
  { to: '/jobs', icon: 'ads_click', label: 'Job Tracker' },
  { to: '/contacts', icon: 'hub', label: 'Contacts' },
  { to: '/cv', icon: 'description', label: 'CV Optimizer' },
  { to: '/achievements', icon: 'emoji_events', label: 'Achievements' },
];

const ADMIN_NAV_ITEMS = [
  { to: '/admin', icon: 'bar_chart', label: 'Overview' },
  { to: '/admin/students', icon: 'group', label: 'Students' },
  { to: '/admin/trainings', icon: 'school', label: 'Trainings' },
  { to: '/admin/achievements', icon: 'emoji_events', label: 'Achievements' },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onOpenTour: () => void;
}

export function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse, onOpenTour }: SidebarProps) {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`h-screen fixed left-0 top-0 border-r border-outline-variant/15 bg-surface-container-low flex flex-col z-50 transition-all duration-300 ease-in-out ${
          collapsed ? 'w-16' : 'w-64'
        } ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        {/* Logo */}
        <div className={`relative flex items-center h-14 px-4 shrink-0 ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <BrandLogo sidebar showWordmark={!collapsed} />
          <button
            onClick={onClose}
            className="md:hidden p-1 rounded-md text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors"
            aria-label="Close menu"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
          {/* Collapse toggle */}
          <button
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center rounded-full bg-surface-container border border-outline-variant/30 text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high transition-colors z-10"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
              {collapsed ? 'chevron_right' : 'chevron_left'}
            </span>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 space-y-1 mt-2 overflow-y-auto">
          {NAV_ITEMS.map(({ to, icon, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onClose}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md transition-all duration-200 text-sm font-medium tracking-tight ${
                  collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'
                } ${
                  isActive
                    ? 'bg-surface-container-high text-on-surface'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container'
                }`
              }
            >
              <span className="material-symbols-outlined text-[20px] shrink-0">{icon}</span>
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}

          {user?.role === 'ADMIN' && (
            <div className="pt-4">
              {!collapsed && (
                <p className="px-3 pb-1 text-[10px] uppercase tracking-[0.15em] text-on-surface-variant font-semibold">
                  Admin
                </p>
              )}
              {ADMIN_NAV_ITEMS.map(({ to, icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/admin'}
                  onClick={onClose}
                  title={collapsed ? label : undefined}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md transition-all duration-200 text-sm font-medium tracking-tight ${
                      collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'
                    } ${
                      isActive
                        ? 'bg-surface-container-high text-on-surface'
                        : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container'
                    }`
                  }
                >
                  <span className="material-symbols-outlined text-[20px] shrink-0">{icon}</span>
                  {!collapsed && <span>{label}</span>}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="mt-auto pt-3 pb-3 border-t border-outline-variant/15 px-2 space-y-1">
          {!collapsed && (
            <div className="px-3 py-2 mb-1 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-on-surface truncate">{user?.email}</p>
                <p className="text-[10px] text-on-surface-variant uppercase tracking-wider">Member</p>
              </div>
              <NavLink
                to="/settings"
                onClick={onClose}
                className={({ isActive }) =>
                  `inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
                    isActive
                      ? 'bg-surface-container-high text-on-surface'
                      : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                  }`
                }
                aria-label="Settings"
                title="Settings"
              >
                <span className="material-symbols-outlined text-[18px]">settings</span>
              </NavLink>
            </div>
          )}

          {collapsed && (
            <NavLink
              to="/settings"
              onClick={onClose}
              title="Settings"
              className={({ isActive }) =>
                `w-full flex items-center justify-center rounded-md px-2 py-2.5 transition-colors ${
                  isActive
                    ? 'bg-surface-container-high text-on-surface'
                    : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                }`
              }
              aria-label="Settings"
            >
              <span className="material-symbols-outlined text-[20px]">settings</span>
            </NavLink>
          )}

          <button
            onClick={onOpenTour}
            title={collapsed ? 'App Tour' : undefined}
            className={`w-full flex items-center gap-3 text-on-surface-variant hover:text-on-surface transition-colors text-sm font-medium rounded-md hover:bg-surface-container ${
              collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'
            }`}
          >
            <span className="material-symbols-outlined text-[20px]">tour</span>
            {!collapsed && <span>App Tour</span>}
          </button>

          <button
            onClick={signOut}
            title={collapsed ? 'Sign out' : undefined}
            className={`w-full flex items-center gap-3 text-on-surface-variant hover:text-on-surface transition-colors text-sm font-medium rounded-md hover:bg-surface-container ${
              collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'
            }`}
          >
            <span className="material-symbols-outlined text-[20px]">logout</span>
            {!collapsed && <span>Sign out</span>}
          </button>

          <button
            onClick={toggleTheme}
            title={collapsed ? (theme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}
            className={`w-full flex items-center gap-3 text-on-surface-variant hover:text-on-surface transition-colors text-sm font-medium rounded-md hover:bg-surface-container ${
              collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'
            }`}
          >
            <span className="material-symbols-outlined text-[20px]">
              {theme === 'dark' ? 'light_mode' : 'dark_mode'}
            </span>
            {!collapsed && <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
